export const BrowserFrame = ({ url, children, dark = false }) => (
    <div
        className={`overflow-hidden rounded-xl border shadow-[0_20px_50px_-24px_rgba(15,23,42,0.25)] ${
            dark ? "border-slate-700 bg-ink" : "border-slate-200 bg-white"
        }`}
    >
        <div
            className={`flex items-center gap-2.5 border-b px-3 py-2.5 ${
                dark ? "border-slate-700/70" : "border-slate-100"
            }`}
        >
            <div className="flex gap-1.5">
                <span className="h-2 w-2 rounded-full bg-slate-300" />
                <span className="h-2 w-2 rounded-full bg-slate-300" />
                <span className="h-2 w-2 rounded-full bg-brand/70" />
            </div>
            <div
                className={`mx-auto flex items-center rounded-full px-3 py-1 ${
                    dark ? "bg-slate-800" : "bg-mist"
                }`}
            >
                <span
                    className={`font-mono text-[9px] ${
                        dark ? "text-slate-400" : "text-slate-500"
                    }`}
                >
                    {url}
                </span>
            </div>
            <span className="w-6" />
        </div>
        {children}
    </div>
);

export const FintechMockup = () => (
    <BrowserFrame url="example.com">
        <div className="flex">
            <div className="hidden w-12 flex-col items-center gap-3 border-r border-slate-100 bg-mist py-4 sm:flex">
                <span className="h-5 w-5 rounded-md bg-ink" />
                <span className="h-6 w-6 rounded-lg bg-frost" />
                <span className="h-6 w-6 rounded-lg bg-slate-200/70" />
                <span className="h-6 w-6 rounded-lg bg-slate-200/70" />
            </div>
            <div className="flex-1 p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[8px] font-medium uppercase tracking-widest text-slate-400">
                            Sample balance
                        </p>
                        <p className="text-base font-bold tracking-tight text-ink">
                            $0,000.00
                        </p>
                    </div>
                    <span className="rounded-full bg-ice px-2 py-1 text-[8px] font-semibold text-brand-deep">
                        Sample data
                    </span>
                </div>
                <svg viewBox="0 0 200 56" className="mt-3 w-full" aria-hidden="true">
                    <polyline
                        points="0,44 25,38 50,41 75,30 100,33 125,22 150,26 175,12 200,8"
                        fill="none"
                        stroke="#3B82F6"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                    />
                    <circle cx="200" cy="8" r="3" fill="#3B82F6" />
                </svg>
                <div className="mt-3 space-y-2">
                    {[72, 48, 88].map((w, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <span className="h-4 w-4 rounded bg-slate-200/80" />
                            <span className="h-1.5 flex-1 rounded-full bg-slate-100">
                                <span
                                    className="block h-full rounded-full bg-slate-300"
                                    style={{ width: `${w}%` }}
                                />
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </BrowserFrame>
);

export const EditorialMockup = () => (
    <BrowserFrame url="example.com">
        <div className="p-4">
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold tracking-[0.2em] text-ink">
                    STUDIO NAME
                </span>
                <div className="flex gap-2">
                    <span className="h-1 w-6 rounded-full bg-slate-200" />
                    <span className="h-1 w-6 rounded-full bg-slate-200" />
                </div>
            </div>
            <p className="mt-4 max-w-[70%] text-sm font-bold leading-snug tracking-tight text-ink">
                A short headline about the studio goes here.
            </p>
            <div className="mt-3 grid grid-cols-3 items-end gap-2">
                <div className="h-20 rounded-lg bg-cloud" />
                <div className="h-28 rounded-lg bg-frost" />
                <div className="h-16 rounded-lg bg-ice" />
            </div>
            <div className="mt-3 flex items-center justify-between">
                <div className="space-y-1">
                    <span className="block h-1 w-16 rounded-full bg-slate-200" />
                    <span className="block h-1 w-10 rounded-full bg-slate-200" />
                </div>
                <span className="rounded-full border border-slate-200 px-2.5 py-1 text-[8px] font-semibold text-slate-500">
                    View gallery
                </span>
            </div>
        </div>
    </BrowserFrame>
);

export const TerminalMockup = () => (
    <BrowserFrame url="example.com" dark>
        <div className="p-4 font-mono text-[10px] leading-relaxed">
            <p className="text-slate-500">$ npm install example-sdk</p>
            <p className="mt-1 text-slate-400">
                <span className="text-blue-400">import</span>{" "}
                <span className="text-slate-200">{"{ Client }"}</span>{" "}
                <span className="text-blue-400">from</span>{" "}
                <span className="text-emerald-400">'example-sdk'</span>
            </p>
            <p className="mt-1 text-slate-400">
                <span className="text-blue-400">const</span>{" "}
                <span className="text-slate-200">client</span>{" "}
                <span className="text-slate-500">=</span>{" "}
                <span className="text-blue-400">new</span>{" "}
                <span className="text-slate-200">Client()</span>
            </p>
            <p className="mt-1 text-slate-400">
                <span className="text-blue-400">await</span>{" "}
                <span className="text-slate-200">client.deploy(</span>
                <span className="text-emerald-400">'live'</span>
                <span className="text-slate-200">)</span>
                <span className="ml-1 inline-block h-3 w-1.5 animate-[blink-caret_1.1s_step-end_infinite] bg-blue-400 align-middle" />
            </p>
            <div className="mt-3 flex items-center gap-2">
                <span className="rounded-md bg-slate-800 px-2.5 py-1.5 text-[9px] text-slate-300">
                    Ready in seconds
                </span>
                <span className="rounded-md bg-blue-500/15 px-2.5 py-1.5 text-[9px] text-blue-300">
                    Sample output
                </span>
            </div>
        </div>
    </BrowserFrame>
);

export const ProductMockup = () => (
    <BrowserFrame url="example.com">
        <div className="flex gap-4 p-4">
            <div className="flex h-28 w-24 shrink-0 items-center justify-center rounded-xl bg-ice">
                <span className="h-12 w-8 rounded-t-full rounded-b-lg bg-frost" />
            </div>
            <div className="flex-1">
                <p className="text-[8px] font-semibold uppercase tracking-widest text-brand-deep">
                    Product category
                </p>
                <p className="mt-1 text-xs font-bold tracking-tight text-ink">
                    Product Name
                </p>
                <div className="mt-1.5 flex gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <span key={i} className="h-1.5 w-1.5 rounded-full bg-brand" />
                    ))}
                </div>
                <div className="mt-2.5 flex items-center justify-between rounded-full bg-ink py-1.5 pl-3 pr-1.5">
                    <span className="text-[9px] font-semibold text-white">Price · Add to bag</span>
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[10px] font-bold text-ink">
                        +
                    </span>
                </div>
            </div>
        </div>
    </BrowserFrame>
);
